import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLingui } from "@lingui/react/macro";
import { Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { recipeKeys } from "@/hooks/recipes/keys";
import { recipeRequests } from "@/lib/api/requests/recipes";
import { getApiErrorMessage } from "@/lib/api/api";

interface CategorySettingsCardProps {
  canManage: boolean;
}

/**
 * Settings → Configuration → Recipe categories (T-1006).
 *
 * Categories already had a home — `/recipes/sections`, stored on the
 * organization — so this surfaces that rather than starting a second store.
 * Two places holding the tenant's categories would eventually hold two
 * different answers.
 *
 * A category in use by a recipe is still listed here, because the list a cook
 * sees is the pinned ones plus whatever recipes actually carry.
 */
export function CategorySettingsCard({ canManage }: CategorySettingsCardProps) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const [entry, setEntry] = useState("");

  const { data, isLoading, isError } = useQuery({
    queryKey: [...recipeKeys.categories(), null],
    queryFn: () => recipeRequests.getCategories(),
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: recipeKeys.categories() });

  const add = useMutation({
    mutationFn: (name: string) => recipeRequests.createSection(name),
    onSuccess: () => {
      setEntry("");
      invalidate();
      toast.success(t`Category added`);
    },
    onError: (err) => toast.error(getApiErrorMessage(err, t`Failed to add the category`)),
  });

  const remove = useMutation({
    mutationFn: (name: string) => recipeRequests.deleteSection(name),
    onSuccess: () => {
      invalidate();
      toast.success(t`Category removed`);
    },
    onError: (err) => toast.error(getApiErrorMessage(err, t`Failed to remove the category`)),
  });

  const isSaving = add.isPending || remove.isPending;

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t`Recipe categories`}</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-10 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (isError || !data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t`Recipe categories`}</CardTitle>
          <CardDescription>{t`The categories could not be loaded.`}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const submit = () => {
    const name = entry.trim();
    if (name === "") return;
    if (data.some((c) => c.toLowerCase() === name.toLowerCase())) {
      toast.error(t`"${name}" is already a category`);
      return;
    }
    add.mutate(name);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t`Recipe categories`}</CardTitle>
        <CardDescription>
          {t`How this kitchen groups its recipes. A category added here can be chosen on any recipe in this organization.`}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {data.length === 0 ? (
          <p className="text-sm text-(--color-text-secondary)">
            {t`No categories yet. Add one and it becomes available on every recipe.`}
          </p>
        ) : (
          <ul className="flex flex-wrap gap-2">
            {data.map((category) => (
              <li
                key={category}
                className="flex items-center gap-1.5 rounded-full border border-(--color-border) bg-(--color-surface-muted) py-1 pl-3 pr-1.5 text-sm"
              >
                <span>{category}</span>
                {canManage && (
                  <button
                    type="button"
                    aria-label={t`Remove ${category}`}
                    disabled={isSaving}
                    onClick={() => remove.mutate(category)}
                    className="rounded-full p-0.5 text-(--color-text-secondary) hover:bg-(--color-surface-page) hover:text-(--color-text-primary) disabled:opacity-50"
                  >
                    <X className="size-3.5" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {canManage && (
          <div className="flex items-center gap-2">
            <Input
              aria-label={t`Add a recipe category`}
              value={entry}
              maxLength={60}
              disabled={isSaving}
              placeholder={t`e.g. Sauces`}
              onChange={(e) => setEntry(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  submit();
                }
              }}
              className="max-w-xs"
            />
            <Button onClick={submit} disabled={entry.trim() === "" || isSaving} className="gap-1.5">
              {isSaving ? <Loader2 className="size-3.5 animate-spin" /> : <Plus className="size-3.5" />}
              {t`Add`}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
